import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@assessment/components/atoms/button";
import SystemCheck from "../stepper-form/steps/systemCheck";
import Modal from "@assessment/components/atoms/modal";
import FaqQuestionAndAnswerModal from "../faq/faqQuetionAndAnsModal";

import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { ProctoringFunctionsContext } from "@assessment/contexts/proctoringFunctionsContext";

import { requestFullScreen } from "@assessment/utils/global/fullScreenFunctions";

import GL from "../../../../../assets/GlobalLogic_logo.svg";
import "@lxp-app/components/molecules/rich-text-viewer/richTextViewer.css";
import GetHeroIcon from "@assessment/utils/getHeroIcons";
import FormateTime from "@assessment/utils/formateTime";
import AssessmentTimer from "../../assessmentTimer";
import { InstructionsArr } from "@assessment/utils/instructionsArr";
import Instructions from "../../instructions";
import useAssessmentTimer from "@assessment/hooks/useAssessmentTimer";
import hitachiLogo from "@lxp-app/assets/svg/hitachiLogo.svg";
import glLogo from "@lxp-app/assets/png/glLogo.png";
import hitachiDarkLogo from "@lxp-app/assets/svg/hitachiDarkLogo.svg";
import glDarkLogo from "@lxp-app/assets/svg/GlDarkLogo.svg";
import GL_DARK from "@assessment/assets/GlobalLogic_dark_logo.svg";
import { differentiateToasts } from "@assessment/utils/toast/differentiateToasts";
import { useSelector } from "react-redux";
import { RootState } from "@lxp-app/store/store";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";
import AudioVideoCheck from "../stepper-form/steps/audioCheck";
import ProgressNotStarted from "./progress-not-started/progressNotStarted";
import { generateUniqueId } from "../stepper-form/generateId";
import ContentDeleteModal from "@lxp-app/components/molecules/modal-children/contentDeleteModal";
import { useUpdateSessionIdMutation } from "@assessment/store/slices/api/assessmentApiSlice";
import { useParams } from "react-router-dom";
import MultipleTabConfirmationModal from "../../modal-children/multipleTabConfirmationModal";

const InProgress = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [newBrowser, setNewBrowser] = useState<boolean>(false);

  const user = useAppSelector((state) => state.UserInfoSlice.profile);
  const hasAssessmentRecOrgs = user?.hasAssessmentRecOrgs;
  const { isDarkMode } = useSelector((state: RootState) => state.DarkmodeSlice);
  const isHitachiOrg = useSelector(
    (state: RootState) => state.UserInfoSlice.profile.isHitachiOrg
  );
  const preAssessmentData = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData
  );

  const { timeLeft } = useAssessmentTimer({
    initialTime: preAssessmentData?.ongoingQpDuration || 0
  });

  const savedId = useAppSelector((state) => state.AssessmentSlice.visitorId);
  // const timeLeft = useAppSelector(
  //   (store) => store.AssessmentSlice.timeLeft
  // );

  const { scheduleId } = useParams();

  const context = useContext(ProctoringFunctionsContext);
  if (!context) {
    throw new Error("Context Error");
  }
  const { startProctoring } = context;

  const [isFaqModalOpen, setFaqModalOpen] = useState(false);
  const [updateTabId] = useUpdateSessionIdMutation();
  const [activeView, setActiveView] = useState<
    "instructions" | "systemCheck" | "audioVideoCheck" | "stepper"
  >("instructions");

  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  let numberOfQuestions: number = 0;
  let isSubjective: number =
    preAssessmentData?.questionTypeCountMap?.subjective;

  if (preAssessmentData?.questionTypeCountMap) {
    numberOfQuestions = Object.values(
      preAssessmentData.questionTypeCountMap
    ).reduce((sum: number, value: number) => sum + value, 0);
  }

  const qpDuration = preAssessmentData?.qpDuration;
  const videoProctoring = preAssessmentData?.videoProctoring;
  const browserProctoring = preAssessmentData?.browserProctoring;
  const instructionArrValue = InstructionsArr({
    qpInstruction: preAssessmentData.qpInstruction,
    scheduleInstruction: preAssessmentData.scheduleInstruction
  });

  const clickHandler = async () => {
    let tabId = sessionStorage.getItem("assessmentTabId");
    let visitorId = localStorage.getItem("assessmentVisitorId");
    let existingId = `${visitorId}-${tabId}`;
    const arr = savedId.split("-");
    if (
      savedId &&
      existingId &&
      savedId != null &&
      existingId != null &&
      savedId != "" &&
      existingId != "" &&
      savedId == existingId
    ) {
      if (
        (videoProctoring || isSubjective > 0) &&
        activeView === "instructions"
      ) {
        videoProctoringEnabled();
      }
      if (
        !videoProctoring &&
        isSubjective == 0 &&
        activeView === "instructions"
      ) {
        videoProctoringDisabled();
      }
    } else {
      if (arr[0] != visitorId) {
        setNewBrowser(true);
        setModalMessage(`This assessment is currently in progress on another browser or device.
Continuing here will end that session and sign you out from the other device.Would you like to continue?`);
      } else {
        setModalMessage(`Assessment open in another tab. Switching here will close the current assessment session in the other tab.
Would you like to continue?`);
        setNewBrowser(false);
      }
      handleModal();
    }
  };

  const videoProctoringEnabled = () => {
    if (isSubjective > 0 && !videoProctoring) setActiveView("audioVideoCheck");
    else if (isSubjective == 0 && videoProctoring) setActiveView("systemCheck");
    else {
      dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(2));
      setActiveView("stepper");
    }
  };

  const videoProctoringDisabled = async () => {
    dispatch(AssessmentSliceActions.mutateShowWarningModal(false));
    const proctoringStarted = await startProctoring();
    if (videoProctoring && !proctoringStarted) {
      differentiateToasts(
        "assessment.systemCheck.unableToStartSecureProctoring"
      );
      return;
    }
    dispatch(AssessmentSliceActions.mutateAssessmentHasStarted(true));
    if (browserProctoring || videoProctoring) requestFullScreen(dispatch);
  };

  const handleModal = () => {
    setModalOpen(!modalOpen);
  };

  const onContinue = async () => {
    try {
      const tabId = await generateUniqueId();
      if (tabId) {
        const body = {
          ongoingId: ongoingId,
          scheduleId: scheduleId,
          sessionId: tabId
        };
        const res1 = await updateTabId(body);
        if (res1) dispatch(AssessmentSliceActions.mutateVisitorId(tabId));

        if (
          (videoProctoring || isSubjective > 0) &&
          activeView === "instructions"
        ) {
          videoProctoringEnabled();
        }
        if (
          !videoProctoring &&
          isSubjective == 0 &&
          activeView === "instructions"
        ) {
          videoProctoringDisabled();
        }
        handleModal();
      }
    } catch (error: any) {
      console.log("error", error);
    }
  };

  return (
    <>
      <div className="h-full bg-bkg rounded-[50px] flex flex-col lg:flex-row max-w-[1400px] w-full pr-1">
        {/* Left */}
        <div className="w-full lg:w-[45%] px-10 lg:px-20 py-10 flex flex-col h-full overflow-y-auto thin-scrollbar">
          <div className="flex justify-between items-center">
            {isHitachiOrg ? (
              <div className="flex items-center h-full">
                <img
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    userSelect: "none",
                    cursor: "pointer"
                  }}
                  className="h-7 md:h-8"
                  src={isDarkMode ? hitachiDarkLogo : hitachiLogo}
                  alt="hitachiLogo"
                />
                <div className="w-0.5 bg-[#D40000] h-9 mx-2"></div>
                <img
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    userSelect: "none",
                    cursor: "pointer"
                  }}
                  className="h-7 md:h-8"
                  src={isDarkMode ? glDarkLogo : glLogo}
                  alt="glLogo"
                />
              </div>
            ) : (
              <img
                src={isDarkMode ? GL_DARK : GL}
                alt="GlobalLogic Logo"
                className="-ml-5 w-56 md:w-72"
              />
            )}

            <div>
              <AssessmentTimer
                qpDuration={Math.abs(preAssessmentData?.ongoingQpDuration)}
              />
            </div>
          </div>
          {/* Name and title */}
          <div className="mt-16 space-y-3">
            <h3 className="text-xl font-medium text-secondary/70">
              {t("assessment.labels.welcomeBack")},{" "}
              <span>
                {user?.name?.firstName || t("assessment.labels.userFallback")}
              </span>
            </h3>
            <h1 className="text-3xl font-semibold text-primary">
              {preAssessmentData?.title}
            </h1>
          </div>
          {/* Meta */}
          <div className="flex flex-wrap items-center justify-between mt-14 gap-y-5 gap-x-10  mb-10">
            <div>
              <h3 className="text-base font-medium">
                {t("assessment.labels.totalDuration")}
              </h3>
              <p className="text-base text-secondary/70">
                {qpDuration} {t("assessment.labels.minutes")}
              </p>
            </div>
            <div>
              <h3 className="text-base font-medium">
                {t("assessment.labels.numberOfQuestions")}
              </h3>
              <p className="text-base text-secondary/70">{numberOfQuestions}</p>
            </div>
            <div>
              <h3 className="text-base font-medium">
                {t("assessment.labels.numberOfSections")}
              </h3>
              <p className="text-base text-secondary/70">
                {preAssessmentData?.no_of_sections}
              </p>
            </div>
          </div>
          <div className="mt-10 bg-[#FEFCE8] flex gap-x-2 px-3 py-2 text-[#854D0E] border border-l-8 border-l-[#FBBF24]">
            <GetHeroIcon
              name={"InformationCircleIcon"}
              variant="solid"
              className="w-5 h-5 text-[#FBBF24]"
            />
            <div>
              You have&nbsp;
              <span className="font-semibold">
                <FormateTime timeInSecond={timeLeft} />
              </span>
              &nbsp;
              {t("assessment.progress.timeRemainingMessage", {
                featureName: orgBasedFeatureName
              })}
              you left off.
            </div>
          </div>
          {/* Button */}
          <div className="my-10 flex-1 flex flex-col justify-end">
            {(videoProctoring || isSubjective > 0) &&
              activeView === "instructions" && (
                <Button
                  buttonColor="primary"
                  variant="contained"
                  className="w-full"
                  onClick={() => {
                    clickHandler();
                  }}
                >
                  {t("assessment.progress.resumeFeature", {
                    featureName: orgBasedFeatureName
                  })}
                </Button>
              )}
            {/* {videoProctoring && activeView === "systemCheck" && (
              <Button
                buttonColor="primary"
                variant="contained"
                className="w-full"
                onClick={clickHandler}
                disabled={processStatus !== "Calibration completed"}
              >
                {"Resume Assessment"}
              </Button>
            )} */}
            {!videoProctoring &&
              isSubjective == 0 &&
              activeView === "instructions" && (
                <Button
                  buttonColor="primary"
                  variant="contained"
                  className="w-full"
                  onClick={clickHandler}
                >
                  {t("assessment.progress.resumeFeature", {
                    featureName: orgBasedFeatureName
                  })}
                </Button>
              )}
          </div>
          {/* Links */}
          <div className="flex justify-start gap-3 mt-10 items-center">
            <span className="text-blue-500 underline underline-offset-1">
              <a
                className="text-sm font-medium cursor-pointer"
                href={
                  hasAssessmentRecOrgs && !isHitachiOrg
                    ? "https://mail.google.com/mail/?view=cm&fs=1&to=amith.c@globallogic.com"
                    : "https://mail.google.com/mail/?view=cm&fs=1&to=support.glx@globallogic.com"
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("assessment.labels.platformHelp")}
              </a>
            </span>
            <div className="w-px h-4 bg-blue-500" />
            <p
              className="text-sm font-medium text-blue-500 underline cursor-pointer"
              onClick={() => setFaqModalOpen(true)}
            >
              {t("assessment.labels.faq")}
            </p>
          </div>
        </div>
        {/* Right */}
        <div className="flex flex-col w-full flex-1 px-4 sm:px-8 md:px-10 lg:px-16 pb-6 pt-10 border-t-2 lg:border-t-0 lg:border-l-2 border-border overflow-y-auto thin-scrollbar h-full">
          {activeView === "instructions" && (
            <Instructions instructionType={instructionArrValue} />
          )}
          {activeView === "systemCheck" && (
            <SystemCheck inResumeScreen={true} />
          )}
          {activeView === "audioVideoCheck" && (
            <AudioVideoCheck inResumeScreen={true} />
          )}
          {activeView === "stepper" && (
            <ProgressNotStarted
              isResume={true}
              orgBasedFeatureName={orgBasedFeatureName || "assessment"}
            />
          )}
        </div>
      </div>
      <Modal
        open={isFaqModalOpen}
        dialogueClassName="w-[100%] sm:w-[50%] max-h-[75vh] overflow-y-auto thin-scrollbar sm:p-6"
        // dialogueClassName="sm:max-w-fit sm:mx-10"
        // dialogueClassName= "sm:max-w-3xl p-4"
        childDialogTitle={t("assessment.labels.frequentlyAskedQuestions")}
        closeBtn={true}
        children={<FaqQuestionAndAnswerModal setOpen={setFaqModalOpen} />}
      />

      <Modal
        open={modalOpen}
        setOpen={handleModal}
        childDialogTitle={
          newBrowser
            ? t("assessment.progress.continueOnThisDevice")
            : t("assessment.progress.switchToThisTab")
        }
        dialogueClassName="sm:max-w-3xl"
        children={
          <MultipleTabConfirmationModal
            isShow={modalOpen}
            onContinue={() => {
              onContinue();
            }}
            setOpen={handleModal}
            buttonName={
              newBrowser
                ? t("assessment.progress.continueYourAssessmentHere")
                : t("assessment.progress.switchHere")
            }
            heading=""
            message={modalMessage}
          />
        }
      />
    </>
  );
};

export default InProgress;
